// ============================================================================
// BirthdaysModal — lightweight modal for the dashboard Birthdays / Anniversaries
// tiles. Workforce directory is out of scope, so this is a modal, not a route
// (spec §3.1). Birthdays show month + day only (no year — DOB redaction);
// anniversaries show the date + years of service.
// ============================================================================
import { Cake, CalendarHeart, X } from 'lucide-react';
import { Skeleton } from '../../shared/components';
import { usePeopleDates } from './hooks/use-onboarding-data';
import { fmtDateShort } from './utils';
import { overlayClickClose } from '../../shared/overlay-close';

function yearsSince(d: string): number {
  const start = new Date(d + 'T00:00:00Z');
  const now = new Date();
  let y = now.getUTCFullYear() - start.getUTCFullYear();
  const m = now.getUTCMonth() - start.getUTCMonth();
  if (m < 0 || (m === 0 && now.getUTCDate() < start.getUTCDate())) y -= 1;
  return Math.max(y, 0);
}

export default function BirthdaysModal({
  mode,
  onClose,
}: {
  mode: 'birthday' | 'anniversary';
  onClose: () => void;
}) {
  const { birthdays, anniversaries, loading, error, dobFieldMissing, hireFieldMissing } =
    usePeopleDates();
  const rows = mode === 'birthday' ? birthdays : anniversaries;
  const fieldMissing = mode === 'birthday' ? dobFieldMissing : hireFieldMissing;
  const Icon = mode === 'birthday' ? Cake : CalendarHeart;
  const title = mode === 'birthday' ? 'Birthdays this month' : 'Anniversaries this month';
  const monthName = new Date().toLocaleDateString('en-US', { month: 'long' });

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-tcs-scrim/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      {...overlayClickClose(onClose)}
    >
      <div className="w-full max-w-md rounded-2xl bg-tcs-overlay shadow-xl" onClick={(e) => e.stopPropagation()}>
        <header className="flex items-center justify-between border-b border-tcs-border px-5 py-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-tcs-text">
            <Icon className="h-4 w-4 text-violet-600" /> {title} · {monthName}
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded p-1 hover:bg-tcs-surface">
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="max-h-[60vh] overflow-y-auto p-4">
          {error ? (
            <p className="text-xs text-st-danger-fg">Couldn't load: {error}</p>
          ) : fieldMissing ? (
            <p className="text-xs text-st-warn-fg">
              Field missing — flag for HR to backfill. (0)
            </p>
          ) : loading ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-8" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <p className="text-xs text-tcs-text-muted">None this month.</p>
          ) : (
            <ul className="divide-y divide-tcs-border">
              {rows.map((r) => (
                <li key={r.employee_id} className="flex items-center justify-between py-2 text-sm">
                  <span className="text-tcs-text">{r.name}</span>
                  <span className="text-xs text-tcs-text-muted">
                    {mode === 'birthday'
                      ? fmtDateShort(r.date)
                      : `${fmtDateShort(r.date)} · ${yearsSince(r.date)} yr`}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
